import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { meetingService, Meeting } from '../../services/meetingService';
import { meetingTextService, MeetingText } from '../../services/meetingTextService'; // MeetingText used for sectionMeetingTexts
import { diagnosesService, Diagnosis } from '../../services/diagnosesService';
import { MedicationSection, MedicationSectionHandle } from '../medications/MedicationSection';
import { medicationService, Medication } from '../../services/medicationService';
import { formatDate } from '../../utils/dateUtils';

const ADHD_DECISION_OPTIONS = [
  { value: 'ADHD_NOT_DIAGNOSED', label: 'ADHD Not Diagnosed' },
  { value: 'NO_ADHD_MEDICATION_RECOMMENDED', label: 'No ADHD medication recommended' },
  { value: 'ADHD_MEDICATION_RECOMMENDED', label: 'ADHD medication recommended' },
  { value: 'ADHD_MEDICATION_RECOMMENDED_DEFERRED', label: 'ADHD medication recommended — deferred' },
];
const TITRATION_OPTIONS = [
  { value: 'STANDARD_TITRATION', label: 'Standard titration' },
  { value: 'SLOW_TITRATION', label: 'Slow titration' },
];
const MONITORING_OPTIONS = [
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'TWICE_WEEKLY', label: 'Twice weekly' },
];
const DEFERRAL_REASON_OPTIONS = [
  'ECG required',
  'Blood tests required',
  'GP medical info required',
  'Psychiatric history clarification required',
  'Other specialist opinion',
  'Other',
];

// Textarea that starts at 2 lines and grows with content — no scrollbar
const AutoResizeTextarea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = ({ style, ...props }) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20;
    const minHeight = lineHeight * 2 + parseFloat(getComputedStyle(el).paddingTop) + parseFloat(getComputedStyle(el).paddingBottom);
    el.style.height = `${Math.max(el.scrollHeight, minHeight)}px`;
  }, [props.value]);
  return (
    <textarea
      ref={ref}
      style={{ resize: 'none', overflow: 'hidden', ...style }}
      {...props}
    />
  );
};

interface SummarySectionConfig {
  type: 'text' | 'diagnostic' | 'medication';
  name: string;
  label: string;
}

interface DoctorSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  meeting: Meeting;
  onSuccess?: () => void;
  clinicLanguage?: string;
  clinicState?: string | null;
}

type TabType = 'summary' | 'notes';

export const DoctorSummaryModal: React.FC<DoctorSummaryModalProps> = ({
  isOpen,
  onClose,
  meeting,
  onSuccess,
  clinicLanguage,
  clinicState,
}) => {
  const isHebrew = clinicLanguage === 'he';
  const isUK = clinicState === 'UK';

  const [activeTab, setActiveTab] = useState<TabType>('summary');
  const [hasTranscript, setHasTranscript] = useState(meeting.has_transcript || meeting.has_rt_transcript);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  // Recipients dialog state
  const [showRecipientsDialog, setShowRecipientsDialog] = useState(false);
  const [sendToPatient, setSendToPatient] = useState(true);
  const [patientEmailInput, setPatientEmailInput] = useState('');
  const [patientEmailError, setPatientEmailError] = useState('');
  const [sendToGP, setSendToGP] = useState(true);
  const [gpEmailInput, setGpEmailInput] = useState('');
  const [gpEmailError, setGpEmailError] = useState('');
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);

  // Text sections: keyed by section name
  const [sectionTexts, setSectionTexts] = useState<Record<string, string>>({});
  const [originalSectionTexts, setOriginalSectionTexts] = useState<Record<string, string>>({});
  // Existing MeetingText records keyed by section name
  const [sectionMeetingTexts, setSectionMeetingTexts] = useState<Record<string, MeetingText>>({});

  // Meeting notes tab fields (read-only)
  const [psySummaryAndDiscussion, setPsySummaryAndDiscussion] = useState('');
  const [psyRecommendations, setPsyRecommendations] = useState('');

  // Diagnosis state
  const [primaryDiagnostic, setPrimaryDiagnostic] = useState('');
  const [primaryDiagSearch, setPrimaryDiagSearch] = useState('');
  const [primaryDiagResults, setPrimaryDiagResults] = useState<Diagnosis[]>([]);
  const [showPrimaryDropdown, setShowPrimaryDropdown] = useState(false);
  const [secondaryDiagnostics, setSecondaryDiagnostics] = useState<{ code: string; label: string }[]>([]);
  const [secondaryDiagSearch, setSecondaryDiagSearch] = useState('');
  const [secondaryDiagResults, setSecondaryDiagResults] = useState<Diagnosis[]>([]);
  const [showSecondaryDropdown, setShowSecondaryDropdown] = useState(false);

  const [diagnosticNotes, setDiagnosticNotes] = useState('');
  const [originalDiagnosticNotes, setOriginalDiagnosticNotes] = useState('');

  const [originalPrimaryDiagnostic, setOriginalPrimaryDiagnostic] = useState('');
  const [originalSecondaryDiagnostics, setOriginalSecondaryDiagnostics] = useState<{ code: string; label: string }[]>([]);

  const primaryDiagTouchedRef = useRef(false);
  const secondaryDiagTouchedRef = useRef(false);
  const medicationSectionRef = useRef<MedicationSectionHandle>(null);

  // UK ADHD medication decision state (mirrors treatment tab in MeetingDetailPanel)
  const [adhdMedicationDecision, setAdhdMedicationDecision] = useState('');
  const [adhdNotDiagnosedReason, setAdhdNotDiagnosedReason] = useState('');
  const [noAdhdMedicationReasonAndNotes, setNoAdhdMedicationReasonAndNotes] = useState('');
  const [adhdMedicationDeferredReasonAndNotes, setAdhdMedicationDeferredReasonAndNotes] = useState('');
  const [adhdMedicationDeferredReasons, setAdhdMedicationDeferredReasons] = useState<string[]>([]);
  const [medicationId, setMedicationId] = useState<number | null>(null);
  const [titrationPlan, setTitrationPlan] = useState('');
  const [initialPlanNotes, setInitialPlanNotes] = useState('');
  const [monitoringPlan, setMonitoringPlan] = useState('');
  const [additionalMonitoringNotes, setAdditionalMonitoringNotes] = useState('');
  const [medications, setMedications] = useState<Medication[]>([]);

  const sections: SummarySectionConfig[] = useMemo(
    () => meeting.meeting_type?.summary_type?.summary_sections || [],
    [meeting.meeting_type?.summary_type?.summary_sections] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const meetingTypeName = meeting.meeting_type?.name ?? 'Doctor Meeting';
  const meetingDate = formatDate(meeting.scheduled_at);

  // Hebrew/English labels
  const t = useMemo(() => ({
    primaryDiag: isHebrew ? 'אבחנה ראשית' : 'Primary Diagnosis',
    secondaryDiag: isHebrew ? 'אבחנה משנית' : 'Secondary Diagnosis',
    searchPrimary: isHebrew ? 'חפש קוד ICD-10 או שם...' : 'Search ICD-10 code or name...',
    searchSecondary: isHebrew ? 'חפש להוסיף קוד ICD-10...' : 'Search to add ICD-10 code...',
    tabSummary: isHebrew ? 'סיכום' : 'Summary',
    tabNotes: isHebrew ? 'הערות פגישה' : 'Meeting Notes',
    summaryAndDiscussion: isHebrew ? 'סיכום ודיון' : 'Summary and Discussion',
    recommendations: isHebrew ? 'המלצות' : 'Recommendations',
  }), [isHebrew]);

  // ── Load texts helper ───────────────────────────────────────────────────────
  const loadTexts = useCallback(async (meetingId: number) => {
    const texts = await meetingTextService.getMeetingTexts(meetingId, 'summary_section');
    const textsMap: Record<string, MeetingText> = {};
    const textValues: Record<string, string> = {};
    const aiFallback: Record<string, MeetingText> = {};
    texts.forEach((t) => {
      if (!t.name) return;
      if (!t.is_ai_generated) {
        textsMap[t.name] = t;
        textValues[t.name] = t.content;
      } else {
        if (!aiFallback[t.name]) aiFallback[t.name] = t;
      }
    });
    Object.keys(aiFallback).forEach((name) => {
      if (!textsMap[name]) {
        textValues[name] = aiFallback[name].content;
      }
    });
    return { textsMap, textValues };
  }, []);

  // ── Load data on open ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;

    setIsLoading(true);
    setActiveTab('summary');
    primaryDiagTouchedRef.current = false;
    secondaryDiagTouchedRef.current = false;

    Promise.all([
      meetingService.getMeeting(meeting.id),
      loadTexts(meeting.id),
    ])
      .then(async ([fullMeeting, { textsMap, textValues }]) => {
        setSectionMeetingTexts(textsMap);
        setSectionTexts(textValues);
        setOriginalSectionTexts({ ...textValues });
        setHasTranscript(fullMeeting.has_transcript || fullMeeting.has_rt_transcript);

        setPsySummaryAndDiscussion(fullMeeting.psy_summary_and_discussion || '');
        setPsyRecommendations(fullMeeting.psy_recommendations || '');

        const notesVal = fullMeeting.diagnostic_notes || '';
        setDiagnosticNotes(notesVal);
        setOriginalDiagnosticNotes(notesVal);

        const primCode = fullMeeting.primary_diagnostic || '';
        setPrimaryDiagnostic(primCode);
        setOriginalPrimaryDiagnostic(primCode);
        if (primCode) {
          try {
            const results = await diagnosesService.searchDiagnoses(primCode);
            const match = results.find((d) => d.code === primCode);
            setPrimaryDiagSearch(match ? `${primCode} — ${match.Diagnosis}` : primCode);
          } catch {
            setPrimaryDiagSearch(primCode);
          }
        } else {
          setPrimaryDiagSearch('');
        }

        setSecondaryDiagSearch('');
        if (fullMeeting.secondary_diagnostic) {
          const codes = fullMeeting.secondary_diagnostic
            .split(',')
            .map((c) => c.trim())
            .filter(Boolean);
          const items = await Promise.all(
            codes.map(async (code) => {
              try {
                const results = await diagnosesService.searchDiagnoses(code);
                const match = results.find((d) => d.code === code);
                return { code, label: match ? `${code} — ${match.Diagnosis}` : code };
              } catch {
                return { code, label: code };
              }
            })
          );
          setSecondaryDiagnostics(items);
          setOriginalSecondaryDiagnostics(items);
        } else {
          setSecondaryDiagnostics([]);
          setOriginalSecondaryDiagnostics([]);
        }

        // UK ADHD medication decision
        if (isUK) {
          setAdhdMedicationDecision(fullMeeting.adhd_medication_decision || '');
          setAdhdNotDiagnosedReason(fullMeeting.adhd_not_diagnosed_reason || '');
          setNoAdhdMedicationReasonAndNotes(fullMeeting.no_adhd_medication_reason_and_notes || '');
          setAdhdMedicationDeferredReasonAndNotes(fullMeeting.adhd_medication_deferred_reason_and_notes || '');
          setAdhdMedicationDeferredReasons(fullMeeting.adhd_medication_deferred_reasons || []);
          setMedicationId((fullMeeting as any).medication_id ?? null);
          setTitrationPlan(fullMeeting.titration_plan || '');
          setInitialPlanNotes((fullMeeting as any).initial_plan_notes || '');
          setMonitoringPlan(fullMeeting.monitoring_plan || '');
          setAdditionalMonitoringNotes((fullMeeting as any).additional_monitoring_notes || '');
          medicationService.getMedications().then(setMedications).catch(() => {});
        }
      })
      .catch(() => toast.error('Failed to load meeting data'))
      .finally(() => setIsLoading(false));
  }, [isOpen, meeting.id, loadTexts, isUK]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Debounced primary diagnosis search ────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (!primaryDiagTouchedRef.current) return;
      if (primaryDiagSearch.length >= 2) {
        try {
          const results = await diagnosesService.searchDiagnoses(primaryDiagSearch);
          setPrimaryDiagResults(results.slice(0, 20));
          setShowPrimaryDropdown(true);
        } catch {
          setPrimaryDiagResults([]);
        }
      } else {
        setPrimaryDiagResults([]);
        setShowPrimaryDropdown(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [primaryDiagSearch]);

  // ── Debounced secondary diagnosis search ──────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (!secondaryDiagTouchedRef.current) return;
      if (secondaryDiagSearch.length >= 2) {
        try {
          const results = await diagnosesService.searchDiagnoses(secondaryDiagSearch);
          setSecondaryDiagResults(results.slice(0, 20));
          setShowSecondaryDropdown(true);
        } catch {
          setSecondaryDiagResults([]);
        }
      } else {
        setSecondaryDiagResults([]);
        setShowSecondaryDropdown(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [secondaryDiagSearch]);

  // ── Unsaved changes detection ──────────────────────────────────────────────
  const hasUnsavedChanges = useCallback((): boolean => {
    for (const section of sections.filter((s) => s.type === 'text')) {
      const current = sectionTexts[section.name] ?? '';
      const original = originalSectionTexts[section.name] ?? '';
      if (current !== original) return true;
    }
    if (diagnosticNotes !== originalDiagnosticNotes) return true;
    if (primaryDiagnostic !== originalPrimaryDiagnostic) return true;
    const currentSecCodes = secondaryDiagnostics.map((d) => d.code).sort().join(',');
    const originalSecCodes = originalSecondaryDiagnostics.map((d) => d.code).sort().join(',');
    if (currentSecCodes !== originalSecCodes) return true;
    return false;
  }, [
    sections,
    sectionTexts,
    originalSectionTexts,
    diagnosticNotes,
    originalDiagnosticNotes,
    primaryDiagnostic,
    originalPrimaryDiagnostic,
    secondaryDiagnostics,
    originalSecondaryDiagnostics,
  ]);

  // ── Preview PDF in modal ──────────────────────────────────────────────────
  const handlePreviewPdf = useCallback(async () => {
    setIsGeneratingPdf(true);
    try {
      const blob = await meetingService.generateSummaryPdf(meeting.id);
      const url = window.URL.createObjectURL(blob);
      setPdfPreviewUrl(url);
    } catch (err) {
      console.error('Failed to generate PDF', err);
      toast.error('Failed to generate PDF');
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [meeting.id]);

  const handleClosePdfPreview = useCallback(() => {
    if (pdfPreviewUrl) {
      window.URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
    }
  }, [pdfPreviewUrl]);

  // ── Copy all text sections to clipboard ───────────────────────────────────
  const handleCopy = useCallback(() => {
    const textSections = sections.filter((s) => s.type === 'text');
    const combined = textSections
      .map((s) => {
        const content = sectionTexts[s.name] ?? '';
        return content ? `${s.label}:\n${content}` : '';
      })
      .filter(Boolean)
      .join('\n\n');
    if (!combined) {
      toast('No text content to copy');
      return;
    }
    navigator.clipboard
      .writeText(combined)
      .then(() => toast.success('Copied to clipboard'))
      .catch(() => toast.error('Failed to copy'));
  }, [sections, sectionTexts]);

  // ── Copy single note to clipboard ─────────────────────────────────────────
  const handleCopyNote = useCallback((content: string) => {
    navigator.clipboard
      .writeText(content)
      .then(() => toast.success('Copied to clipboard'))
      .catch(() => toast.error('Failed to copy'));
  }, []);

  // ── Save all sections ──────────────────────────────────────────────────────
  const saveAll = useCallback(async () => {
    const ops: Promise<unknown>[] = [];
    const newTextCreates: { name: string; promise: Promise<MeetingText> }[] = [];

    for (const section of sections.filter((s) => s.type === 'text')) {
      const content = sectionTexts[section.name] ?? '';
      const original = originalSectionTexts[section.name] ?? '';
      if (content === original) continue;
      const existing = sectionMeetingTexts[section.name];
      if (existing && !existing.is_ai_generated) {
        ops.push(
          meetingTextService.updateMeetingText(existing.id, {
            content,
            title: section.label,
          })
        );
      } else {
        const createPromise = meetingTextService.createMeetingText({
          name: section.name,
          title: section.label,
          content,
          text_type: 'summary_section',
          meeting_id: meeting.id,
        });
        newTextCreates.push({ name: section.name, promise: createPromise });
        ops.push(createPromise);
      }
    }

    const meetingUpdate: Record<string, unknown> = {};

    const hasDiagSection = sections.some((s) => s.type === 'diagnostic');
    if (hasDiagSection) {
      meetingUpdate.primary_diagnostic = primaryDiagnostic || null;
      meetingUpdate.secondary_diagnostic =
        secondaryDiagnostics.length > 0
          ? secondaryDiagnostics.map((d) => d.code).join(',')
          : null;
      meetingUpdate.diagnostic_notes = diagnosticNotes || null;
    }

    const hasMedSection = sections.some((s) => s.type === 'medication');
    if (hasMedSection) {
      if (isUK) {
        meetingUpdate.adhd_medication_decision = adhdMedicationDecision || null;
        meetingUpdate.adhd_not_diagnosed_reason =
          adhdMedicationDecision === 'ADHD_NOT_DIAGNOSED' ? adhdNotDiagnosedReason || null : null;
        meetingUpdate.no_adhd_medication_reason_and_notes =
          adhdMedicationDecision === 'NO_ADHD_MEDICATION_RECOMMENDED' ? noAdhdMedicationReasonAndNotes || null : null;
        meetingUpdate.adhd_medication_deferred_reasons =
          adhdMedicationDecision === 'ADHD_MEDICATION_RECOMMENDED_DEFERRED'
            ? adhdMedicationDeferredReasons.length > 0 ? adhdMedicationDeferredReasons : null
            : null;
        meetingUpdate.adhd_medication_deferred_reason_and_notes =
          adhdMedicationDecision === 'ADHD_MEDICATION_RECOMMENDED_DEFERRED' ? adhdMedicationDeferredReasonAndNotes || null : null;
        meetingUpdate.medication_id =
          adhdMedicationDecision === 'ADHD_MEDICATION_RECOMMENDED' ? medicationId : null;
        meetingUpdate.titration_plan =
          adhdMedicationDecision === 'ADHD_MEDICATION_RECOMMENDED' ? titrationPlan || null : null;
        meetingUpdate.initial_plan_notes =
          adhdMedicationDecision === 'ADHD_MEDICATION_RECOMMENDED' ? initialPlanNotes || null : null;
        meetingUpdate.monitoring_plan =
          adhdMedicationDecision === 'ADHD_MEDICATION_RECOMMENDED' ? monitoringPlan || null : null;
        meetingUpdate.additional_monitoring_notes =
          adhdMedicationDecision === 'ADHD_MEDICATION_RECOMMENDED' ? additionalMonitoringNotes || null : null;
      } else if (medicationSectionRef.current) {
        ops.push(medicationSectionRef.current.save());
      }
    }

    if (Object.keys(meetingUpdate).length > 0) {
      ops.push(meetingService.updateMeeting(meeting.id, meetingUpdate as any));
    }

    await Promise.all(ops);

    if (newTextCreates.length > 0) {
      const created = await Promise.all(newTextCreates.map((nc) => nc.promise.then((mt) => ({ name: nc.name, mt }))));
      setSectionMeetingTexts((prev) => {
        const updated = { ...prev };
        created.forEach(({ name, mt }) => { updated[name] = mt; });
        return updated;
      });
    }

    setOriginalSectionTexts({ ...sectionTexts });
    setOriginalDiagnosticNotes(diagnosticNotes);
    setOriginalPrimaryDiagnostic(primaryDiagnostic);
    setOriginalSecondaryDiagnostics([...secondaryDiagnostics]);
  }, [
    sections,
    sectionTexts,
    originalSectionTexts,
    sectionMeetingTexts,
    meeting.id,
    diagnosticNotes,
    primaryDiagnostic,
    secondaryDiagnostics,
    isUK,
    adhdMedicationDecision,
    adhdNotDiagnosedReason,
    noAdhdMedicationReasonAndNotes,
    adhdMedicationDeferredReasons,
    adhdMedicationDeferredReasonAndNotes,
    medicationId,
    titrationPlan,
    initialPlanNotes,
    monitoringPlan,
    additionalMonitoringNotes,
  ]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveAll();
      toast.success('Saved successfully');
      onSuccess?.();
    } catch {
      toast.error('Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const validateMedicationDecision = (): string | null => {
    if (!isUK) return null;
    if (!sections.some((s) => s.type === 'medication')) return null;
    if (!adhdMedicationDecision) return 'Please select an ADHD Medication Decision before sending.';
    if (adhdMedicationDecision === 'ADHD_NOT_DIAGNOSED' && !adhdNotDiagnosedReason.trim())
      return 'Please fill in the reason ADHD was not diagnosed.';
    if (adhdMedicationDecision === 'NO_ADHD_MEDICATION_RECOMMENDED' && !noAdhdMedicationReasonAndNotes.trim())
      return 'Please fill in the reason and notes for no ADHD medication.';
    if (adhdMedicationDecision === 'ADHD_MEDICATION_RECOMMENDED') {
      if (!medicationId) return 'Please select a medication.';
      if (!titrationPlan) return 'Please select a titration plan.';
      if (!monitoringPlan) return 'Please select a monitoring plan.';
    }
    if (adhdMedicationDecision === 'ADHD_MEDICATION_RECOMMENDED_DEFERRED') {
      if (!adhdMedicationDeferredReasons.length) return 'Please select at least one reason for deferring medication.';
      if (!adhdMedicationDeferredReasonAndNotes.trim()) return 'Please fill in the deferred medication reason and notes.';
    }
    return null;
  };

  const openRecipientsDialog = async () => {
    const medicationError = validateMedicationDecision();
    if (medicationError) {
      toast.error(medicationError);
      return;
    }
    setIsSaving(true);
    try {
      await saveAll();
    } catch {
      toast.error('Failed to save');
      return;
    } finally {
      setIsSaving(false);
    }
    setPatientEmailInput(meeting.patient.email || '');
    setGpEmailInput(meeting.patient.gp_email || '');
    setSendToPatient(!!meeting.patient.email);
    setSendToGP(!!meeting.patient.gp_email);
    setPatientEmailError('');
    setGpEmailError('');
    setShowRecipientsDialog(true);
  };

  const handleSend = async () => {
    let valid = true;
    if (sendToPatient) {
      if (!patientEmailInput || !isValidEmail(patientEmailInput)) {
        setPatientEmailError('Please enter a valid email address');
        valid = false;
      } else {
        setPatientEmailError('');
      }
    }
    if (sendToGP) {
      if (!gpEmailInput || !isValidEmail(gpEmailInput)) {
        setGpEmailError('Please enter a valid email address');
        valid = false;
      } else {
        setGpEmailError('');
      }
    }
    if (!valid) return;

    setShowRecipientsDialog(false);
    setIsSending(true);
    try {
      const recipients: { patientEmail?: string | null; gpEmail?: string | null } = {};
      if (sendToPatient && patientEmailInput) recipients.patientEmail = patientEmailInput;
      if (sendToGP && gpEmailInput) recipients.gpEmail = gpEmailInput;
      await meetingService.sendSummary(meeting.id, recipients, true);
      toast.success('Summary sent successfully');
      onSuccess?.();
      onClose();
    } catch {
      toast.error('Failed to send summary');
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = useCallback(() => {
    if (hasUnsavedChanges()) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to close?')) return;
    }
    onClose();
  }, [hasUnsavedChanges, onClose]);

  if (!isOpen) return null;

  const contentDir = isHebrew ? 'rtl' : 'ltr';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1400] p-4">
      <div
        className="bg-[#B8DDD0] rounded-3xl w-full shadow-2xl relative max-w-5xl h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3 flex-shrink-0">
          {/* Tabs */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setActiveTab('summary')}
              className={`px-5 py-2 rounded-xl text-sm font-semibold transition-all ${
                activeTab === 'summary'
                  ? 'bg-white text-[#366A75] shadow-sm'
                  : 'text-[#366A75]/70 hover:text-[#366A75] hover:bg-white/40'
              }`}
            >
              {t.tabSummary}
            </button>
            <button
              onClick={() => setActiveTab('notes')}
              className={`px-5 py-2 rounded-xl text-sm font-semibold transition-all ${
                activeTab === 'notes'
                  ? 'bg-white text-[#366A75] shadow-sm'
                  : 'text-[#366A75]/70 hover:text-[#366A75] hover:bg-white/40'
              }`}
            >
              {t.tabNotes}
            </button>
          </div>

          {/* Close button */}
          <button
            onClick={handleClose}
            disabled={isSaving}
            className="text-[#366A75] hover:text-[#2D5A60] transition-colors disabled:opacity-50 p-1"
            title="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── Body ────────────────────────────────────────────────────────── */}
        <div className="flex-1 min-h-0 flex flex-col mx-4 mb-4 rounded-2xl overflow-hidden bg-[#EBF5F5]">
          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto" dir={contentDir}>
            {/* Meeting info row — document-style header (scrolls with content) */}
            <div className="relative px-6 py-4 border-b border-[#B8DDD0]/60" dir="ltr">
              <div className="flex items-start">
                {/* LEFT: Patient info */}
                <div className="flex-1 text-sm text-[#366A75] space-y-0.5">
                  <div className="font-semibold">{meeting.patient.full_name}</div>
                  <div>{meetingDate}</div>
                  {meeting.patient.id_number && (
                    <div>{`${isUK ? 'NHS Number' : 'ת.ז'}: ${meeting.patient.id_number}`}</div>
                  )}
                </div>

                {/* CENTER: meeting type title */}
                <div className="flex-1 text-center">
                  <div className="text-lg font-bold text-[#366A75]">{meetingTypeName}</div>
                </div>

                {/* RIGHT: Doctor / clinic info */}
                <div className="flex-1 text-sm text-[#366A75] space-y-0.5 text-right" dir="rtl">
                  {isHebrew ? (
                    <>
                      <div className="flex items-center justify-start gap-1">
                        {activeTab === 'summary' && (
                          <button
                            type="button"
                            onClick={handleCopy}
                            disabled={isSaving}
                            title="Copy all text sections"
                            className="p-1 rounded-lg hover:bg-[#B8DDD0] transition-all disabled:opacity-40 text-[#366A75] flex-shrink-0"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        )}
                        <span>{`נבדק ע"י: ד"ר ${meeting.therapist.full_name}`}
                          {meeting.therapist.license_number ? ` מספר רישיון:` : ''}
                        </span>
                      </div>
                      {meeting.therapist.license_number && (
                        <div>{meeting.therapist.license_number}</div>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center justify-end gap-1">
                      <span className="font-semibold">{meeting.therapist.full_name}</span>
                      {activeTab === 'summary' && (
                        <button
                          type="button"
                          onClick={handleCopy}
                          disabled={isSaving}
                          title="Copy all text sections"
                          className="p-1 rounded-lg hover:bg-[#B8DDD0] transition-all disabled:opacity-40 text-[#366A75] flex-shrink-0"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Transcript not available warning */}
            {!hasTranscript && (
              <div className="px-6 py-2 text-center border-b border-[#B8DDD0]/60">
                <span className="text-red-500 font-semibold text-sm">Transcript not available</span>
              </div>
            )}

            <div className="px-6 py-4">
            {isLoading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#366A75]" />
              </div>
            ) : activeTab === 'summary' ? (
              /* ── Summary Tab ────────────────────────────────────────── */
              <div className="space-y-5">
                {sections.map((section, idx) => {
                  if (section.type === 'text') {
                    return (
                      <div key={`${section.name}-${idx}`}>
                        {/* Section header */}
                        <div className="flex items-center justify-between bg-white rounded-t-xl px-4 py-2.5 border border-[#B8DDD0]/50">
                          <span className="text-sm font-semibold text-[#366A75]">{section.label}</span>
                          <button
                            type="button"
                            onClick={() => {
                              const content = sectionTexts[section.name] ?? '';
                              navigator.clipboard.writeText(content)
                                .then(() => toast.success('Copied'))
                                .catch(() => toast.error('Failed to copy'));
                            }}
                            className="p-1 rounded hover:bg-[#EBF5F5] transition-all text-[#366A75]/60 hover:text-[#366A75]"
                            title="Copy section"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                        <AutoResizeTextarea
                          dir={isHebrew ? 'rtl' : 'ltr'}
                          value={sectionTexts[section.name] ?? ''}
                          onChange={(e) =>
                            setSectionTexts((prev) => ({ ...prev, [section.name]: e.target.value }))
                          }
                          disabled={isSaving}
                          className="w-full px-4 py-3 rounded-b-xl bg-[#B5D9D4]/40 border border-t-0 border-[#B8DDD0]/50 focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent disabled:opacity-60"
                          style={{ fontFamily: 'Arial, sans-serif', fontSize: '13px', lineHeight: '1.6', color: '#366A75' }}
                          placeholder={`${section.label}...`}
                        />
                      </div>
                    );
                  }

                  if (section.type === 'diagnostic') {
                    return (
                      <div key={`${section.name}-${idx}`} className="space-y-4">
                        {/* Primary Diagnosis */}
                        <div>
                          <div className="flex items-center justify-between bg-white rounded-t-xl px-4 py-2.5 border border-[#B8DDD0]/50">
                            <span className="text-sm font-semibold text-[#366A75]">{t.primaryDiag}</span>
                          </div>
                          <div className="bg-[#B5D9D4]/40 border border-t-0 border-[#B8DDD0]/50 rounded-b-xl px-4 py-3">
                            <div className="relative">
                              <input
                                type="text"
                                value={primaryDiagSearch}
                                onChange={(e) => {
                                  primaryDiagTouchedRef.current = true;
                                  setPrimaryDiagSearch(e.target.value);
                                  if (!e.target.value) setPrimaryDiagnostic('');
                                }}
                                onBlur={() => setTimeout(() => setShowPrimaryDropdown(false), 150)}
                                onFocus={() => primaryDiagResults.length > 0 && setShowPrimaryDropdown(true)}
                                disabled={isSaving}
                                placeholder={t.searchPrimary}
                                className="w-full border border-[#B8DDD0] rounded-lg px-3 py-2 text-sm bg-white text-[#366A75] focus:ring-2 focus:ring-[#366A75] focus:border-transparent outline-none disabled:opacity-60"
                                dir="ltr"
                              />
                              {showPrimaryDropdown && primaryDiagResults.length > 0 && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto" dir="ltr">
                                  {primaryDiagResults.map((d) => (
                                    <button
                                      key={d.code}
                                      type="button"
                                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 focus:bg-gray-50"
                                      onMouseDown={() => {
                                        setPrimaryDiagnostic(d.code);
                                        setPrimaryDiagSearch(`${d.code} — ${d.Diagnosis}`);
                                        setShowPrimaryDropdown(false);
                                      }}
                                    >
                                      <span className="font-mono text-xs text-teal-700">{d.code}</span>
                                      {' — '}{d.Diagnosis}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Secondary Diagnosis */}
                        <div>
                          <div className="flex items-center justify-between bg-white rounded-t-xl px-4 py-2.5 border border-[#B8DDD0]/50">
                            <span className="text-sm font-semibold text-[#366A75]">{t.secondaryDiag}</span>
                          </div>
                          <div className="bg-[#B5D9D4]/40 border border-t-0 border-[#B8DDD0]/50 rounded-b-xl px-4 py-3 space-y-2">
                            {secondaryDiagnostics.length > 0 && (
                              <div className="flex flex-wrap gap-1.5">
                                {secondaryDiagnostics.map((d) => (
                                  <span
                                    key={d.code}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-[#B8DDD0] text-xs text-[#366A75]"
                                    dir="ltr"
                                  >
                                    <span className="font-mono">{d.label}</span>
                                    <button
                                      type="button"
                                      disabled={isSaving}
                                      onClick={() =>
                                        setSecondaryDiagnostics((prev) =>
                                          prev.filter((x) => x.code !== d.code)
                                        )
                                      }
                                      className="text-[#366A75]/60 hover:text-[#366A75] leading-none disabled:opacity-50"
                                    >
                                      ×
                                    </button>
                                  </span>
                                ))}
                              </div>
                            )}
                            <div className="relative">
                              <input
                                type="text"
                                value={secondaryDiagSearch}
                                onChange={(e) => {
                                  secondaryDiagTouchedRef.current = true;
                                  setSecondaryDiagSearch(e.target.value);
                                }}
                                onBlur={() => setTimeout(() => setShowSecondaryDropdown(false), 150)}
                                onFocus={() => secondaryDiagResults.length > 0 && setShowSecondaryDropdown(true)}
                                disabled={isSaving}
                                placeholder={t.searchSecondary}
                                className="w-full border border-[#B8DDD0] rounded-lg px-3 py-2 text-sm bg-white text-[#366A75] focus:ring-2 focus:ring-[#366A75] focus:border-transparent outline-none disabled:opacity-60"
                                dir="ltr"
                              />
                              {showSecondaryDropdown && secondaryDiagResults.length > 0 && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto" dir="ltr">
                                  {secondaryDiagResults
                                    .filter((d) => !secondaryDiagnostics.some((x) => x.code === d.code))
                                    .map((d) => (
                                      <button
                                        key={d.code}
                                        type="button"
                                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 focus:bg-gray-50"
                                        onMouseDown={() => {
                                          setSecondaryDiagnostics((prev) => [
                                            ...prev,
                                            { code: d.code, label: `${d.code} — ${d.Diagnosis}` },
                                          ]);
                                          setSecondaryDiagSearch('');
                                          setShowSecondaryDropdown(false);
                                        }}
                                      >
                                        <span className="font-mono text-xs text-teal-700">{d.code}</span>
                                        {' — '}{d.Diagnosis}
                                      </button>
                                    ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Diagnostic Notes */}
                        <div>
                          <div className="flex items-center justify-between bg-white rounded-t-xl px-4 py-2.5 border border-[#B8DDD0]/50">
                            <span className="text-sm font-semibold text-[#366A75]">
                              {isHebrew ? 'הערות אבחנה' : 'Diagnostic Notes'}
                            </span>
                          </div>
                          <textarea
                            dir={isHebrew ? 'rtl' : 'ltr'}
                            value={diagnosticNotes}
                            onChange={(e) => setDiagnosticNotes(e.target.value)}
                            disabled={isSaving}
                            rows={2}
                            className="w-full px-4 py-3 rounded-b-xl bg-[#B5D9D4]/40 border border-t-0 border-[#B8DDD0]/50 focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent resize-none disabled:opacity-60"
                            style={{ fontFamily: 'Arial, sans-serif', fontSize: '13px', lineHeight: '1.6', color: '#366A75' }}
                            placeholder={isHebrew ? 'הערות אבחנה...' : 'Diagnostic notes...'}
                          />
                        </div>
                      </div>
                    );
                  }

                  if (section.type === 'medication') {
                    if (!isUK) {
                      // Israel (general): editable MedicationSection
                      return (
                        <div key={`${section.name}-${idx}`} dir="ltr">
                          <MedicationSection
                            ref={medicationSectionRef}
                            meetingId={meeting.id}
                            showToggle={true}
                          />
                        </div>
                      );
                    }

                    // UK (ADHD): editable medication decision UI (same as treatment tab)
                    return (
                      <div key={`${section.name}-${idx}`} className="space-y-3">
                        {/* Decision radio group */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            ADHD Medication Decision
                          </label>
                          <div className="space-y-2">
                            {ADHD_DECISION_OPTIONS.map(({ value, label }) => (
                              <label key={value} className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name={`adhdDecision-${section.name}`}
                                  value={value}
                                  checked={adhdMedicationDecision === value}
                                  onChange={() => setAdhdMedicationDecision(value)}
                                  disabled={isSaving}
                                  className="text-teal-600 focus:ring-teal-500"
                                />
                                <span className="text-sm text-gray-700">{label}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* ADHD Not Diagnosed */}
                        {adhdMedicationDecision === 'ADHD_NOT_DIAGNOSED' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                            <textarea
                              value={adhdNotDiagnosedReason}
                              onChange={(e) => setAdhdNotDiagnosedReason(e.target.value)}
                              disabled={isSaving}
                              rows={3}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none disabled:opacity-60"
                              placeholder="Reason ADHD was not diagnosed..."
                            />
                          </div>
                        )}

                        {/* No medication */}
                        {adhdMedicationDecision === 'NO_ADHD_MEDICATION_RECOMMENDED' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Reason & Notes</label>
                            <textarea
                              value={noAdhdMedicationReasonAndNotes}
                              onChange={(e) => setNoAdhdMedicationReasonAndNotes(e.target.value)}
                              disabled={isSaving}
                              rows={3}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none disabled:opacity-60"
                              placeholder="Reason and notes for no ADHD medication..."
                            />
                          </div>
                        )}

                        {/* Medication recommended */}
                        {adhdMedicationDecision === 'ADHD_MEDICATION_RECOMMENDED' && (
                          <div className="space-y-3 pl-4 border-l-2 border-teal-200">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Medication</label>
                              <select
                                value={medicationId ?? ''}
                                onChange={(e) => setMedicationId(e.target.value ? Number(e.target.value) : null)}
                                disabled={isSaving}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none disabled:opacity-60"
                              >
                                <option value="">Select medication...</option>
                                {medications.map((m) => (
                                  <option key={m.id} value={m.id}>{m.name}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Titration Plan</label>
                              <div className="flex gap-4">
                                {TITRATION_OPTIONS.map(({ value, label }) => (
                                  <label key={value} className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="radio"
                                      name={`titration-${section.name}`}
                                      value={value}
                                      checked={titrationPlan === value}
                                      onChange={() => setTitrationPlan(value)}
                                      disabled={isSaving}
                                      className="text-teal-600 focus:ring-teal-500"
                                    />
                                    <span className="text-sm text-gray-700">{label}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Initial Plan Notes</label>
                              <textarea
                                value={initialPlanNotes}
                                onChange={(e) => setInitialPlanNotes(e.target.value)}
                                disabled={isSaving}
                                rows={3}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none disabled:opacity-60"
                                placeholder="Initial plan notes..."
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Monitoring Plan</label>
                              <div className="flex gap-4">
                                {MONITORING_OPTIONS.map(({ value, label }) => (
                                  <label key={value} className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="radio"
                                      name={`monitoring-${section.name}`}
                                      value={value}
                                      checked={monitoringPlan === value}
                                      onChange={() => setMonitoringPlan(value)}
                                      disabled={isSaving}
                                      className="text-teal-600 focus:ring-teal-500"
                                    />
                                    <span className="text-sm text-gray-700">{label}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Additional Monitoring Notes</label>
                              <textarea
                                value={additionalMonitoringNotes}
                                onChange={(e) => setAdditionalMonitoringNotes(e.target.value)}
                                disabled={isSaving}
                                rows={3}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none disabled:opacity-60"
                                placeholder="Additional monitoring notes..."
                              />
                            </div>
                          </div>
                        )}

                        {/* Deferred */}
                        {adhdMedicationDecision === 'ADHD_MEDICATION_RECOMMENDED_DEFERRED' && (
                          <div className="space-y-3 pl-4 border-l-2 border-teal-200">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Reason for deferral</label>
                              <div className="space-y-2">
                                {DEFERRAL_REASON_OPTIONS.map((reason) => (
                                  <label key={reason} className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={adhdMedicationDeferredReasons.includes(reason)}
                                      disabled={isSaving}
                                      onChange={(e) => {
                                        setAdhdMedicationDeferredReasons(prev =>
                                          e.target.checked ? [...prev, reason] : prev.filter(r => r !== reason)
                                        );
                                      }}
                                      className="rounded text-teal-600 focus:ring-teal-500"
                                    />
                                    <span className="text-sm text-gray-700">{reason}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Details</label>
                              <textarea
                                value={adhdMedicationDeferredReasonAndNotes}
                                onChange={(e) => setAdhdMedicationDeferredReasonAndNotes(e.target.value)}
                                disabled={isSaving}
                                rows={3}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none disabled:opacity-60"
                                placeholder="Additional details..."
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }

                  return null;
                })}

                {sections.length === 0 && (
                  <p className="text-sm text-[#366A75]/60 italic text-center py-8">
                    No sections configured for this meeting type.
                  </p>
                )}
              </div>
            ) : (
              /* ── Meeting Notes Tab ──────────────────────────────────── */
              <div className="space-y-5" dir={contentDir}>
                {[
                  { label: t.summaryAndDiscussion, value: psySummaryAndDiscussion },
                  { label: t.recommendations, value: psyRecommendations },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <div className="flex items-center justify-between bg-white rounded-t-xl px-4 py-2.5 border border-[#B8DDD0]/50">
                      <span className="text-sm font-semibold text-[#366A75]">{label}</span>
                      <button
                        type="button"
                        onClick={() => handleCopyNote(value)}
                        className="p-1 rounded hover:bg-[#EBF5F5] transition-all text-[#366A75]/60 hover:text-[#366A75]"
                        title="Copy"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                    <div
                      className="w-full px-4 py-3 rounded-b-xl bg-[#B5D9D4]/40 border border-t-0 border-[#B8DDD0]/50 text-sm text-[#366A75] whitespace-pre-wrap min-h-[80px]"
                      style={{ fontFamily: 'Arial, sans-serif', lineHeight: '1.6' }}
                      dir={isHebrew ? 'rtl' : 'ltr'}
                    >
                      {value || <span className="text-[#366A75]/40 italic">—</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            </div>
          </div>
        </div>

        {/* ── Footer ──────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-6 pb-5 flex-shrink-0">
          {/* Left: PDF preview icon */}
          <div className="flex items-center">
            {meeting.meeting_type?.summary_type?.pdf_layout && meeting.meeting_type.summary_type.pdf_layout.length > 0 && (
              <button
                type="button"
                onClick={handlePreviewPdf}
                disabled={isSaving || isLoading || isGeneratingPdf}
                title="Preview PDF"
                className="p-2 text-[#3A7380] hover:text-[#2D5A60] hover:bg-[#B8DDD0]/60 rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {isGeneratingPdf ? (
                  <div className="w-5 h-5 border-2 border-[#3A7380] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            )}
          </div>

          {/* Right: action buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={isSaving || isSending || isLoading}
              className="px-7 py-2 bg-[#EBF5F5] text-[#3A7380] rounded-xl shadow-sm hover:bg-[#D0E7E5] transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={openRecipientsDialog}
              disabled={isSaving || isSending || isLoading}
              className="px-7 py-2 bg-[#3A7380] text-white rounded-xl shadow-sm hover:bg-[#2D5A60] transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
            >
              {isSending ? 'Sending...' : 'Send'}
            </button>
            <button
              onClick={handleClose}
              disabled={isSaving || isSending}
              className="px-7 py-2 bg-[#EBF5F5] text-[#3A7380] rounded-xl shadow-sm hover:bg-[#D0E7E5] transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {/* Recipients Dialog */}
      {showRecipientsDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1420]">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-6 pb-2">
              <h2 className="text-xl font-bold text-gray-900">Choose Recipients</h2>
              <button onClick={() => setShowRecipientsDialog(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="px-6 pb-4 text-sm text-gray-500">Please choose who should receive the assessment summary.</p>

            <div className="px-6 space-y-3 pb-6">
              {/* Patient */}
              <div className={`border rounded-xl p-4 transition-colors ${sendToPatient ? 'border-[#3A7380] bg-[#EBF5F5]' : 'border-gray-200 bg-white'}`}>
                <div className="flex items-center justify-between mb-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sendToPatient}
                      onChange={(e) => { setSendToPatient(e.target.checked); setPatientEmailError(''); }}
                      className="w-4 h-4 rounded accent-[#3A7380]"
                    />
                    <span className="font-semibold text-gray-800">Send to Patient</span>
                  </label>
                  <div className="w-9 h-9 rounded-full bg-[#D0E7E5] flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#3A7380]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                </div>
                {sendToPatient && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1 font-medium">Patient Email</p>
                    <input
                      type="email"
                      value={patientEmailInput}
                      onChange={(e) => { setPatientEmailInput(e.target.value); setPatientEmailError(''); }}
                      className={`w-full border rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-1 ${patientEmailError ? 'border-red-400 focus:ring-red-400' : 'border-gray-200 focus:ring-[#3A7380]'}`}
                    />
                    {patientEmailError && <p className="text-xs text-red-500 mt-1">{patientEmailError}</p>}
                  </div>
                )}
              </div>

              {/* GP */}
              <div className={`border rounded-xl p-4 transition-colors ${sendToGP ? 'border-[#4A6FA5] bg-[#EEF2F9]' : 'border-gray-200 bg-white'}`}>
                <div className="flex items-center justify-between mb-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sendToGP}
                      onChange={(e) => { setSendToGP(e.target.checked); setGpEmailError(''); }}
                      className="w-4 h-4 rounded accent-[#4A6FA5]"
                    />
                    <span className="font-semibold text-gray-800">Send to GP</span>
                  </label>
                  <div className="w-9 h-9 rounded-full bg-[#D6E4F5] flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#4A6FA5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
                {sendToGP && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1 font-medium">GP Email</p>
                    <input
                      type="email"
                      value={gpEmailInput}
                      onChange={(e) => { setGpEmailInput(e.target.value); setGpEmailError(''); }}
                      className={`w-full border rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-1 ${gpEmailError ? 'border-red-400 focus:ring-red-400' : 'border-gray-200 focus:ring-[#4A6FA5]'}`}
                    />
                    {gpEmailError && <p className="text-xs text-red-500 mt-1">{gpEmailError}</p>}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-100 px-6 py-4 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowRecipientsDialog(false)}
                className="px-5 py-2 rounded-xl border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={(!sendToPatient || !patientEmailInput) && (!sendToGP || !gpEmailInput)}
                className="px-5 py-2 rounded-xl bg-[#3A7380] text-white text-sm font-semibold hover:bg-[#2D5A60] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Send Emails
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Preview Modal */}
      {pdfPreviewUrl && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1410] p-6"
          onClick={handleClosePdfPreview}
        >
          <div
            className="bg-[#B8DDD0] rounded-3xl p-4 w-full max-w-5xl h-[90vh] flex flex-col shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleClosePdfPreview}
              className="absolute top-4 right-4 z-10 text-[#366A75] hover:text-[#2D5A60] transition-colors"
              title="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <iframe
              src={pdfPreviewUrl}
              title="Summary PDF"
              className="w-full flex-1 rounded-2xl border-4 border-white/60"
              style={{ minHeight: 0 }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
